/* Asset-Browser ohne DOM: Einträge aus Server-Assets und Editor-Zustand bilden, „fehlend“ bestimmen, Schnellsuche.
 * Ein Asset gilt als fehlend, wenn die Karte es braucht und es im Editor noch nicht geladen ist. */
import { isSoundFile, soundKey } from '../net/sounds.ts';
import { wadLoaded } from './texlist.ts';

export type AssetKind = 'wad' | 'sprite' | 'skybox' | 'sound' | 'model';
export const ASSET_KINDS: AssetKind[] = ['wad', 'sprite', 'skybox', 'sound', 'model'];

/** Teil von AssetInfo, den der Browser braucht */
export interface ServerAsset { path: string; size: number }

export interface AssetEntry {
  kind: AssetKind;
  /** wad/sprite/model: Dateiname (klein); skybox: Name ohne Seite; sound: Schlüssel (Pfad ab sound/) */
  key: string;
  label: string;
  /** Pfade auf dem Server (Skybox: bis zu sechs Seiten) */
  paths: string[];
  size: number;
  loaded: boolean;
  needed: boolean;
  onServer: boolean;
}

/** Was der Editor geladen hat und was die Karte braucht */
export interface AssetState {
  wantedWads: string[];
  /** Namen im Format „datei.wad (Anzahl)“ */
  loadedWadNames: string[];
  wantedSprites: string[];
  loadedSprites: string[];
  /** skyname der Karte */
  wantedSky: string;
  loadedSky: { name: string; sides: number } | null;
  wantedSounds: string[];
  loadedSounds: string[];
  /** Modelle (Dateiname klein), die die Karte braucht, und die geladenen */
  wantedModels?: string[];
  loadedModels?: string[];
  hasSound(key: string): boolean;
}

const SKY_SIDE = /^(.*?)(rt|lf|up|dn|ft|bk)\.tga$/i;
const baseOf = (p: string): string => p.replace(/^.*\//, '');

/** Hauptdatei eines Modells: nameT.mdl und name01.mdl gehören zu name.mdl, wenn es diese Datei gibt */
export function modelMain(key: string, all: readonly string[]): string {
  const set = new Set(all);
  const t = /^(.*)t\.mdl$/.exec(key);
  if (t && set.has(t[1] + '.mdl')) return t[1] + '.mdl';
  const g = /^(.*?)\d\d\.mdl$/.exec(key);
  if (g && set.has(g[1] + '.mdl')) return g[1] + '.mdl';
  return key;
}

/** Art und Schlüssel eines Server-Pfads; null, wenn er zu keiner Art passt (falscher Ordner oder Dateityp, z. B. .txt) */
export function classify(path: string): { kind: AssetKind; key: string } | null {
  const p = path.toLowerCase();
  if (p.startsWith('wads/') && p.endsWith('.wad')) return { kind: 'wad', key: baseOf(p) };
  if (p.startsWith('sprites/') && p.endsWith('.spr')) return { kind: 'sprite', key: baseOf(p) };
  if (p.startsWith('skybox/')) { const m = SKY_SIDE.exec(baseOf(p)); return m ? { kind: 'skybox', key: m[1] } : null; }
  if (p.startsWith('sounds/') && isSoundFile(p)) return { kind: 'sound', key: soundKey(p.slice(7)) };
  if (p.startsWith('models/') && p.endsWith('.mdl')) return { kind: 'model', key: baseOf(p) };
  return null;
}

/** Server-Assets ohne Art (nicht als WAD, Sprite, Skybox-Seite oder Sound erkennbar) */
export const unclassified = (assets: ServerAsset[]): ServerAsset[] => assets.filter((a) => !classify(a.path)).sort((a, b) => a.path.localeCompare(b.path));

/** Fehlend = von der Karte gebraucht und nicht geladen */
export const isMissing = (e: Pick<AssetEntry, 'needed' | 'loaded'>): boolean => e.needed && !e.loaded;

/** Alle Einträge: Server-Assets, dazu lokal geladene und von der Karte gebrauchte, die nicht auf dem Server liegen. Gebrauchte zuerst. */
export function buildEntries(assets: ServerAsset[], st: AssetState): AssetEntry[] {
  const out = new Map<string, AssetEntry>();
  const slot = (kind: AssetKind, key: string, label: string): AssetEntry => {
    const id = kind + ':' + key;
    let e = out.get(id);
    if (!e) { e = { kind, key, label, paths: [], size: 0, loaded: false, needed: false, onServer: false }; out.set(id, e); }
    return e;
  };
  const wantW = new Set(st.wantedWads.map((x) => x.toLowerCase())), wantS = new Set(st.wantedSprites.map((x) => x.toLowerCase()));
  const wantSnd = new Set(st.wantedSounds.map(soundKey));
  const wantSky = st.wantedSky.trim().toLowerCase();

  // Begleitdateien eines Modells (nameT.mdl mit den Texturen, name01.mdl als Sequenzgruppe) gehören zum Eintrag des Modells
  const modelFiles = assets.map((a) => classify(a.path)).filter((c) => c && c.kind === 'model').map((c) => (c as { key: string }).key);
  for (const a of assets) {
    const c = classify(a.path);
    if (!c) continue;
    if (c.kind === 'model') c.key = modelMain(c.key, modelFiles);
    const e = slot(c.kind, c.key, c.kind === 'sound' ? c.key : c.kind === 'skybox' ? c.key : c.kind === 'model' ? c.key : baseOf(a.path));
    e.paths.push(a.path); e.size += a.size; e.onServer = true;
  }
  for (const n of st.loadedWadNames) { const k = n.replace(/ \(\d+\)$/, '').toLowerCase(); slot('wad', k, k.length ? n.replace(/ \(\d+\)$/, '') : k).loaded = true; }
  for (const k of st.loadedSprites) slot('sprite', k.toLowerCase(), k).loaded = true;
  for (const k of st.loadedSounds) slot('sound', k, k).loaded = true;
  for (const k of st.loadedModels || []) slot('model', k.toLowerCase(), k).loaded = true;
  if (st.loadedSky && st.loadedSky.sides >= 6) slot('skybox', st.loadedSky.name.toLowerCase(), st.loadedSky.name).loaded = true;

  for (const w of wantW) slot('wad', w, w);
  for (const s of wantS) slot('sprite', s, s);
  for (const s of wantSnd) slot('sound', s, s);
  const wantM = new Set((st.wantedModels || []).map((x) => x.toLowerCase()));
  for (const m of wantM) slot('model', m, m);
  if (wantSky) slot('skybox', wantSky, wantSky);

  for (const e of out.values()) {
    if (e.kind === 'wad') { e.needed = wantW.has(e.key); e.loaded = e.loaded || wadLoaded(st.loadedWadNames, e.key); }
    else if (e.kind === 'sprite') e.needed = wantS.has(e.key);
    else if (e.kind === 'sound') { e.needed = wantSnd.has(e.key); e.loaded = e.loaded || st.hasSound(e.key); }
    else if (e.kind === 'model') e.needed = wantM.has(e.key);
    else e.needed = e.key === wantSky;
  }
  return Array.from(out.values()).sort((a, b) => Number(b.needed) - Number(a.needed) || a.label.localeCompare(b.label));
}

/** Passt die Anfrage auf den Text? Kleiner ist besser, -1 = keine Übereinstimmung.
 * Jedes Wort der Anfrage muss vorkommen: am Anfang (0), am Wortanfang (1), irgendwo (2) oder verstreut als Buchstabenfolge (3 + Lücken). */
export function matchScore(query: string, text: string): number {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const hay = text.toLowerCase();
  let total = 0;
  for (const w of words) {
    const i = hay.indexOf(w);
    if (i === 0) continue;
    if (i > 0) { total += /[\W_]/.test(hay[i - 1]) ? 1 : 2; continue; }
    let pos = -1, gaps = 0;
    for (const ch of w) {
      const j = hay.indexOf(ch, pos + 1);
      if (j < 0) return -1;
      if (pos >= 0 && j > pos + 1) gaps++;
      pos = j;
    }
    total += 3 + gaps;
  }
  return total;
}

/** Schnellsuche: Treffer nach Güte sortiert (gleich gute behalten ihre Reihenfolge); ohne Anfrage alles in Ausgangsreihenfolge */
export function quickFilter<T>(items: T[], query: string, textOf: (x: T) => string): T[] {
  if (!query.trim()) return items.slice();
  const scored: { x: T; s: number; i: number }[] = [];
  items.forEach((x, i) => { const s = matchScore(query, textOf(x)); if (s >= 0) scored.push({ x, s, i }); });
  return scored.sort((a, b) => a.s - b.s || a.i - b.i).map((r) => r.x);
}

export interface EntryFilter { query: string; /** nur von der Karte benutzte */ onlyUsed: boolean; kind?: AssetKind }

export function filterEntries(entries: AssetEntry[], f: EntryFilter): AssetEntry[] {
  const base = entries.filter((e) => (!f.kind || e.kind === f.kind) && (!f.onlyUsed || e.needed));
  return quickFilter(base, f.query, (e) => e.label);
}

/** Texturen für den Browser: nur die in der Karte benutzten (auch ohne WAD, dann ok = false) oder alle geladenen, benutzte zuerst */
export function textureItems(loaded: string[], used: { name: string; ok: boolean }[], f: { query: string; onlyUsed: boolean }): { name: string; used: boolean; ok: boolean }[] {
  const usedSet = new Set(used.map((u) => u.name.toUpperCase()));
  const base = f.onlyUsed
    ? used.map((u) => ({ name: u.name, used: true, ok: u.ok }))
    : loaded.map((n) => ({ name: n, used: usedSet.has(n.toUpperCase()), ok: true })).sort((a, b) => Number(b.used) - Number(a.used));
  return quickFilter(base, f.query, (x) => x.name);
}

/** Skyboxen brauchen sechs Seiten: welche fehlen auf dem Server für diesen Eintrag? */
export function skySidesOnServer(e: AssetEntry): string[] {
  return e.paths.map((p) => (SKY_SIDE.exec(baseOf(p)) || [])[2]?.toLowerCase()).filter(Boolean) as string[];
}

/** Fügt eine WAD in den wad-Eintrag der Welt ein ("\\valve\\a.wad;\\valve\\b.wad"); schon vorhandene bleiben unverändert.
 * Der Ordner der neuen Datei ist der des ersten Eintrags, sonst "\\valve\\". */
export function addWad(list: string, name: string): string {
  const entries = String(list || '').split(';').map((x) => x.trim()).filter(Boolean);
  const file = name.trim().replace(/^.*[\\/]/, '');
  if (!file) return entries.join(';');
  if (entries.some((x) => x.replace(/^.*[\\/]/, '').toLowerCase() === file.toLowerCase())) return entries.join(';');
  const dir = entries.length ? entries[0].replace(/[^\\/]*$/, '') : '';
  entries.push((dir || '\\valve\\') + file);
  return entries.join(';');
}

/** Wird die geladene Skybox in 3D gezeigt? Nur die der Karte (skyname) oder eine ausdrücklich gewählte Vorschau. */
export function skyVisible(sb: { on: boolean; mats: unknown; name: string; preview?: boolean } | null | undefined, skyname: unknown): boolean {
  if (!sb || !sb.on || !sb.mats) return false;
  return !!sb.preview || sb.name.toLowerCase() === String(skyname || '').trim().toLowerCase();
}
